/**
 * @file <bk-facets>: storefront filtering, sorting and pagination without a full page load.
 *
 * Markup (snippets/facets.liquid, snippets/pagination.liquid and the collection and search sections):
 *   <bk-facets class="facets facets--sidebar" data-section-id="main" data-dialog-id="Facets-main">
 *     .facets__toolbar
 *       form.facets__form                       hidden inputs; every control points at it with form=""
 *       [data-facets-region="toolbar"]          filter trigger + sort select
 *       [data-facets-status]                    polite live region with the result count (never swapped)
 *       [data-facets-region="active"]           active filter pills + "clear filters"
 *     bk-dialog.facets__aside > dialog > [data-facets-region="filters"]
 *     .facets__results[data-facets-region="grid"]
 *       [data-facets-list]                      the product list
 *       [data-facets-more-region] a[data-facets-more]   optional "show more"
 *   </bk-facets>
 *
 * Behaviour:
 * - One FormData call on the toolbar form builds the next URL, wherever the control sits.
 * - That URL is rendered through the Section Rendering API; every [data-facets-region] is swapped
 *   by name, the live status gets the new count as text (so screen readers announce it), and the
 *   URL is pushed with history.pushState. Back and forward re-render from popstate.
 * - Open filter groups, scroll positions, the focused control and its caret survive a swap. When
 *   the focused control disappears (a removed pill, a pagination link), focus moves to a sensible
 *   neighbour instead of falling back to the page top.
 * - "Show more" appends the next page's items and moves focus to the first new one.
 * - Anything unexpected (network error, missing section) falls back to a normal navigation, so the
 *   page always ends up in the requested state.
 * - With JS off nothing here runs: the form is a plain GET form and the pagination items are links.
 *
 * All visible strings come from the server-rendered markup; this module never writes copy.
 */
import { debounce, prefersReducedMotion } from '@theme/utils';
import '@theme/dialog';

const PRICE_DEBOUNCE = 700;
const PRICE_PARAM = /\.(?:gte|lte)$/;
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled])';

/**
 * Quote a value for use inside an attribute selector.
 * @param {string} value
 * @returns {string}
 */
function quoted(value) {
  return `"${String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
}

/**
 * Price fields show the shop's money format ("150 000", "1'250.50"): send the number only.
 * Thousands separators (spaces, narrow spaces, apostrophes) go; the decimal mark stays.
 * @param {string} value
 * @returns {string}
 */
function normalizePrice(value) {
  return value.replace(/[\s   '’]/g, '').replace(/[^\d.,]/g, '');
}

/**
 * Render one section for a URL through the Section Rendering API (`?section_id=`, HTML response).
 * No JSON Accept header on purpose: on collection and product URLs Shopify answers such a request
 * with the resource's JSON instead of the section.
 * @param {URL} url - Page to render the section in context of (its query string is kept)
 * @param {string} sectionId
 * @param {AbortSignal} signal
 * @returns {Promise<Document>}
 */
async function renderSection(url, sectionId, signal) {
  const target = new URL(url);
  target.hash = '';
  target.searchParams.set('section_id', sectionId);
  const response = await fetch(target, { signal });
  if (!response.ok) throw new Error(`Section Rendering API: HTTP ${response.status}`);
  const markup = await response.text();
  const next = new DOMParser().parseFromString(markup, 'text/html');
  if (!next.querySelector('[data-facets-region]')) throw new Error('Facets: the section did not render');
  return next;
}

/** @param {Element | null} element */
function isShown(element) {
  if (!(element instanceof HTMLElement)) return false;
  if (typeof element.checkVisibility === 'function') return element.checkVisibility({ visibilityProperty: true });
  return element.getClientRects().length > 0;
}

export class BkFacets extends HTMLElement {
  /** @type {AbortController | null} */
  #listeners = null;

  /** @type {AbortController | null} */
  #request = null;

  /** @type {MediaQueryList | null} */
  #desktop = null;

  /** @type {ResizeObserver | null} */
  #toolbarObserver = null;

  /** Query string of the state on screen, so popstate and duplicate submits can be ignored. */
  #rendered = '';

  #pricePending = false;

  #applyPrice = debounce(() => {
    this.#pricePending = false;
    this.#submit();
  }, PRICE_DEBOUNCE);

  connectedCallback() {
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;
    this.#rendered = window.location.search;

    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('input', this.#onInput, { signal });
    this.addEventListener('submit', this.#onSubmit, { signal });
    this.addEventListener('click', this.#onClick, { signal });
    window.addEventListener('popstate', this.#onPopState, { signal });
    document.addEventListener('bk:dialog:open', this.#onDialogToggle, { signal });
    document.addEventListener('bk:dialog:close', this.#onDialogToggle, { signal });

    this.#desktop = matchMedia('(min-width: 990px)');
    this.#desktop.addEventListener('change', this.#onBreakpoint, { signal });

    // The sticky sidebar and the results' scroll margin sit under the toolbar: give them its real
    // height, which grows when active-filter pills wrap.
    const toolbar = this.querySelector('.facets__toolbar');
    if (toolbar instanceof HTMLElement && 'ResizeObserver' in window) {
      this.#toolbarObserver = new ResizeObserver(() => {
        this.style.setProperty('--facets-toolbar-h', `${Math.ceil(toolbar.getBoundingClientRect().height)}px`);
      });
      this.#toolbarObserver.observe(toolbar);
    }
  }

  disconnectedCallback() {
    this.#toolbarObserver?.disconnect();
    this.#toolbarObserver = null;
    this.#listeners?.abort();
    this.#listeners = null;
    this.#request?.abort();
    this.#request = null;
    this.#applyPrice.cancel();
    this.#pricePending = false;
  }

  /** @returns {HTMLFormElement | null} */
  get #form() {
    return this.querySelector('form.facets__form');
  }

  /** @returns {any} The <bk-dialog> host of the filter panel (BkDialog API: open, close, isOpen) */
  get #dialogHost() {
    return this.querySelector('bk-dialog.facets__aside');
  }

  /**
   * @param {EventTarget | null} target
   * @returns {target is HTMLInputElement | HTMLSelectElement}
   */
  #isFacetControl(target) {
    const form = this.#form;
    if (!form) return false;
    return (target instanceof HTMLInputElement || target instanceof HTMLSelectElement) && target.form === form;
  }

  /** Next URL from every control associated with the toolbar form (always back to page 1). */
  #urlFromForm() {
    const form = this.#form;
    if (!form) return null;
    const sort = this.querySelector('select[name="sort_by"]');
    const defaultSort = sort instanceof HTMLSelectElement ? sort.dataset.default : undefined;
    const params = new URLSearchParams();
    for (const [key, raw] of new FormData(form)) {
      if (typeof raw !== 'string') continue;
      const value = PRICE_PARAM.test(key) ? normalizePrice(raw.trim()) : raw.trim();
      if (value === '') continue;
      if (key === 'sort_by' && value === defaultSort) continue; // the default order needs no parameter
      params.append(key, value);
    }
    const url = new URL(form.action, window.location.origin);
    url.search = params.toString();
    return url;
  }

  #submit() {
    const url = this.#urlFromForm();
    if (!url || url.search === this.#rendered) return;
    this.#render(url);
  }

  #flushPrice() {
    if (!this.#pricePending) return;
    this.#applyPrice.cancel();
    this.#pricePending = false;
    this.#submit();
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;
    if (!this.#isFacetControl(target)) return;
    if (target.hasAttribute('data-facets-price')) {
      this.#flushPrice();
      return;
    }
    this.#submit();
  };

  /** @param {Event} event */
  #onInput = (event) => {
    const target = event.target;
    if (!this.#isFacetControl(target) || !target.hasAttribute('data-facets-price')) return;
    this.#pricePending = true;
    this.#applyPrice();
  };

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    if (event.target !== this.#form) return;
    event.preventDefault();
    this.#applyPrice.cancel();
    this.#pricePending = false;
    this.#submit();
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    const link = target.closest('a[data-facets-more], a[data-facets-link], .pagination a[href]');
    if (!(link instanceof HTMLAnchorElement) || !this.contains(link)) return;
    if (link.target && link.target !== '_self') return;
    event.preventDefault();
    this.#applyPrice.cancel();
    this.#pricePending = false;

    const url = new URL(link.href, window.location.origin);
    if (link.hasAttribute('data-facets-more')) {
      this.#loadMore(link, url);
      return;
    }
    const paging = link.closest('.pagination') !== null;
    this.#render(url, { scroll: paging, focusResults: paging });
  };

  #onPopState = () => {
    if (window.location.search === this.#rendered) return;
    this.#render(new URL(window.location.href), { push: false });
  };

  /** Keeps the trigger's aria-expanded true to the drawer, even after the toolbar was swapped. */
  #onDialogToggle = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    if (!detail || detail.id !== this.dataset.dialogId) return;
    this.#syncTrigger();
    if (event.type !== 'bk:dialog:close') return;

    this.#flushPrice();
    // A swap may have removed the control that opened the drawer, so focus would stay on a control
    // inside the closed drawer (then drop to the body): land on the new trigger instead.
    requestAnimationFrame(() => {
      const active = document.activeElement;
      const panel = this.querySelector('.facets__dialog');
      const lost = !active || active === document.body || active === document.documentElement;
      if (!lost && !panel?.contains(active)) return;
      const trigger = this.querySelector('[data-dialog-open]');
      if (isShown(trigger)) /** @type {HTMLElement} */ (trigger).focus({ preventScroll: true });
    });
  };

  /** The sidebar layout has no drawer on wide screens: close it when the breakpoint is crossed. */
  #onBreakpoint = () => {
    if (this.dataset.layout !== 'sidebar' || !this.#desktop?.matches) return;
    const host = this.#dialogHost;
    if (host?.isOpen && typeof host.close === 'function') host.close();
  };

  /**
   * @param {URL} url
   * @param {{ push?: boolean, scroll?: boolean, focusResults?: boolean }} [options]
   */
  async #render(url, { push = true, scroll = false, focusResults = false } = {}) {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) {
      window.location.assign(url.toString());
      return;
    }

    this.#request?.abort();
    const request = new AbortController();
    this.#request = request;
    this.#setBusy(true);

    try {
      const next = await renderSection(url, sectionId, request.signal);

      const groups = this.#captureGroups();
      const scrolls = this.#captureScroll();
      const focused = this.#captureFocus();

      this.#swapRegions(next);
      this.#updateStatus(next);
      this.#restoreGroups(groups);
      this.#restoreScroll(scrolls);
      this.#syncTrigger();
      this.#rendered = url.search;
      if (push) {
        const pushed = new URL(url);
        pushed.hash = '';
        history.pushState({ bkFacets: sectionId }, '', pushed.toString());
      }

      if (focusResults) {
        this.#focusResults(scroll);
      } else {
        this.#restoreFocus(focused);
        if (scroll) this.#scrollToResults();
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      window.location.assign(url.toString());
    } finally {
      if (this.#request === request) {
        this.#request = null;
        this.#setBusy(false);
      }
    }
  }

  /**
   * "Show more": append the next page's items, swap the progress and the next link.
   * @param {HTMLAnchorElement} link
   * @param {URL} url
   */
  async #loadMore(link, url) {
    const sectionId = this.dataset.sectionId;
    const list = this.querySelector('[data-facets-list]');
    const region = link.closest('[data-facets-more-region]');
    if (!sectionId || !list || !region) {
      window.location.assign(url.toString());
      return;
    }

    this.#request?.abort();
    const request = new AbortController();
    this.#request = request;
    link.setAttribute('aria-busy', 'true');
    link.setAttribute('aria-disabled', 'true');

    try {
      const next = await renderSection(url, sectionId, request.signal);
      const items = Array.from(next.querySelector('[data-facets-list]')?.children ?? []);
      const nextRegion = next.querySelector('[data-facets-more-region]');
      if (items.length === 0 || !nextRegion) throw new Error('Facets: nothing to append');

      list.append(...items);
      region.innerHTML = nextRegion.innerHTML;
      const first = items[0].querySelector(FOCUSABLE);
      if (first instanceof HTMLElement) first.focus({ preventScroll: prefersReducedMotion() });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      window.location.assign(url.toString());
    } finally {
      if (this.#request === request) this.#request = null;
      if (link.isConnected) {
        link.removeAttribute('aria-busy');
        link.removeAttribute('aria-disabled');
      }
    }
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    const results = this.querySelector('[data-facets-region="grid"]');
    if (!results) return;
    if (busy) results.setAttribute('aria-busy', 'true');
    else results.removeAttribute('aria-busy');
  }

  /** @param {Document} next */
  #swapRegions(next) {
    for (const target of this.querySelectorAll('[data-facets-region]')) {
      const name = target.getAttribute('data-facets-region');
      const source = name ? next.querySelector(`[data-facets-region=${quoted(name)}]`) : null;
      if (source) target.innerHTML = source.innerHTML;
    }
  }

  /** The live region stays in place; only its text changes, which is what gets announced. */
  #updateStatus(next) {
    const status = this.querySelector('[data-facets-status]');
    const text = next.querySelector('[data-facets-status]')?.textContent?.trim();
    if (status && text && status.textContent?.trim() !== text) status.textContent = text;
  }

  /** @returns {Map<string, boolean>} */
  #captureGroups() {
    const state = new Map();
    for (const group of this.querySelectorAll('details.facets__group[data-facet]')) {
      const details = /** @type {HTMLDetailsElement} */ (group);
      if (details.dataset.facet) state.set(details.dataset.facet, details.open);
    }
    return state;
  }

  /** @param {Map<string, boolean>} state */
  #restoreGroups(state) {
    for (const group of this.querySelectorAll('details.facets__group[data-facet]')) {
      const details = /** @type {HTMLDetailsElement} */ (group);
      const key = details.dataset.facet;
      if (key && state.has(key)) details.open = Boolean(state.get(key));
    }
  }

  /** @returns {Map<string, number>} Scroll offsets of the filter panel and of each long option list */
  #captureScroll() {
    const state = new Map();
    const groups = this.querySelector('.facets__groups');
    if (groups) state.set('groups', groups.scrollTop);
    for (const list of this.querySelectorAll('.facets__group[data-facet] .facets__options')) {
      const key = /** @type {HTMLElement} */ (list.closest('.facets__group')).dataset.facet;
      if (key) state.set(key, list.scrollTop);
    }
    return state;
  }

  /** @param {Map<string, number>} state */
  #restoreScroll(state) {
    const groups = this.querySelector('.facets__groups');
    if (groups && state.has('groups')) groups.scrollTop = state.get('groups') ?? 0;
    for (const list of this.querySelectorAll('.facets__group[data-facet] .facets__options')) {
      const key = /** @type {HTMLElement} */ (list.closest('.facets__group')).dataset.facet;
      if (key && state.has(key)) list.scrollTop = state.get(key) ?? 0;
    }
  }

  /** @returns {{ selector: string, caret: number | null, typed: string | null, region: string | null } | null} */
  #captureFocus() {
    const active = document.activeElement;
    if (!(active instanceof HTMLElement) || !this.contains(active)) return null;

    let selector = '';
    if (active.id) selector = `#${CSS.escape(active.id)}`;
    else if (active instanceof HTMLInputElement && active.type === 'checkbox' && active.name) {
      selector = `input[name=${quoted(active.name)}][value=${quoted(active.value)}]`;
    } else if (active instanceof HTMLAnchorElement && active.hasAttribute('data-facets-link')) {
      selector = `a[data-facets-link][href=${quoted(active.getAttribute('href') ?? '')}]`;
    }

    let caret = null;
    let typed = null;
    if (active instanceof HTMLInputElement && active.type === 'text') {
      caret = active.selectionStart;
      typed = active.value;
    }
    const region = active.closest('[data-facets-region]')?.getAttribute('data-facets-region') ?? null;
    return { selector, caret, typed, region };
  }

  /** @param {{ selector: string, caret: number | null, typed: string | null, region: string | null } | null} focused */
  #restoreFocus(focused) {
    if (!focused) return;
    const element = focused.selector ? this.querySelector(focused.selector) : null;
    if (element instanceof HTMLElement && isShown(element)) {
      // A field still being typed in keeps the visitor's own text, not the server's formatting.
      if (focused.typed !== null && element instanceof HTMLInputElement) element.value = focused.typed;
      element.focus({ preventScroll: true });
      if (focused.caret !== null && element instanceof HTMLInputElement) {
        try {
          element.setSelectionRange(focused.caret, focused.caret);
        } catch {
          // Not every input type exposes a selection range; the focus alone is enough.
        }
      }
      return;
    }
    this.#focusFallback(focused.region);
  }

  /**
   * The focused control is gone (a removed pill, "clear filters"): stay close to where it was.
   * @param {string | null} region
   */
  #focusFallback(region) {
    const host = this.#dialogHost;
    const candidates = [];
    if (host?.isOpen) {
      candidates.push(this.querySelector('[data-facets-region="filters"]'));
    } else {
      if (region === 'active') candidates.push(this.querySelector('[data-facets-region="active"]'));
      candidates.push(this.querySelector('[data-facets-region="toolbar"]'));
    }
    for (const container of candidates) {
      if (!container) continue;
      for (const element of container.querySelectorAll(FOCUSABLE)) {
        if (isShown(element)) {
          /** @type {HTMLElement} */ (element).focus({ preventScroll: true });
          return;
        }
      }
    }
    this.#focusResults(false);
  }

  /** @param {boolean} scroll */
  #focusResults(scroll) {
    const results = this.querySelector('[data-facets-region="grid"]');
    if (!(results instanceof HTMLElement)) return;
    if (!results.hasAttribute('tabindex')) results.setAttribute('tabindex', '-1');
    results.focus({ preventScroll: true });
    if (scroll) this.#scrollToResults();
  }

  /** A swapped toolbar carries the server's aria-expanded="false": re-sync it with the drawer. */
  #syncTrigger() {
    const host = this.#dialogHost;
    const trigger = this.querySelector('[data-dialog-open]');
    if (!host || !(trigger instanceof HTMLElement)) return;
    trigger.setAttribute('aria-expanded', host.isOpen ? 'true' : 'false');
  }

  /** Brings the top of the results under the sticky header and toolbar (their scroll margin). */
  #scrollToResults() {
    const results = this.querySelector('[data-facets-region="grid"]');
    if (!(results instanceof HTMLElement)) return;
    const margin = parseFloat(getComputedStyle(results).scrollMarginBlockStart) || 0;
    if (results.getBoundingClientRect().top >= margin) return;
    results.scrollIntoView({ block: 'start', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  }
}

if (!customElements.get('bk-facets')) customElements.define('bk-facets', BkFacets);
